import { useEffect, useState, useCallback } from "react";
import {
  listStockOrders, createStockOrder, updateStockOrder, deleteStockOrder,
  whatsappStockOrder, markStockOrderSent, updateStockOrderStatus, stockOrderPdfUrl,
  listSuppliers, listJobs, apiError,
} from "@/lib/api";
import { Card, ConfirmDelete, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, FileDown, Pencil, Truck, PackageCheck, ClipboardList, AlertTriangle, MessageCircle, ExternalLink } from "lucide-react";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  submitted: { label: "Submitted", cls: "bg-blue-100 text-blue-700" },
  confirmed: { label: "Confirmed", cls: "bg-indigo-100 text-indigo-700" },
  dispatched: { label: "Dispatched", cls: "bg-amber-100 text-amber-700" },
  delivered: { label: "Delivered", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-rose-100 text-rose-700" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.cls}`} data-testid={`stock-status-${status}`}>{m.label}</span>;
}

const supplierLabel = (o) => (o.supplier_snapshot || {}).name || (o.branch_snapshot || {}).branch_name || "—";
const emptyItem = () => ({ description: "", code: "", quantity: 1, unit: "", unit_price: "", notes: "" });
const emptyForm = () => ({ supplier_id: "", order_ref: "", items: [emptyItem()], site_reference: "", delivery_method: "collect", delivery_address: "", notes: "", job_id: "none" });

export function StockOrders({ manage = false, accentClass = "bg-[#1E7A6B] hover:bg-[#186052]", fixedJobId = null }) {
  const [orders, setOrders] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [wa, setWa] = useState(null); // { order, wa_url, message, phone }
  const [waOpened, setWaOpened] = useState(false);
  const [marking, setMarking] = useState(false);
  const [statusFor, setStatusFor] = useState(null);

  const load = useCallback(() => {
    listStockOrders(fixedJobId ? { job_id: fixedJobId } : {}).then(setOrders).catch((e) => { setOrders([]); toast.error(apiError(e)); });
  }, [fixedJobId]);
  const loadSuppliers = useCallback(() => { listSuppliers().then(setSuppliers).catch(() => {}); }, []);
  useEffect(() => { load(); loadSuppliers(); listJobs().then(setJobs).catch(() => {}); }, [load, loadSuppliers]);

  const openNew = () => { loadSuppliers(); setEditing(null); setForm({ ...emptyForm(), job_id: fixedJobId || "none" }); setOpen(true); };
  const openEdit = (o) => {
    loadSuppliers();
    setEditing(o);
    setForm({
      supplier_id: o.supplier_id || "", order_ref: o.order_ref || "",
      items: (o.items && o.items.length ? o.items : [emptyItem()]).map((i) => ({ ...emptyItem(), ...i, unit_price: i.unit_price ?? "" })),
      site_reference: o.site_reference || "", delivery_method: o.delivery_method || "collect",
      delivery_address: o.delivery_address || "", notes: o.notes || "", job_id: o.job_id || "none",
    });
    setOpen(true);
  };

  const setItem = (idx, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [k]: v } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.supplier_id) return toast.error("Select a supplier");
    const items = form.items.filter((i) => (i.description || "").trim()).map((i) => ({
      ...i, quantity: Number(i.quantity) || 0,
      unit_price: i.unit_price === "" || i.unit_price === null ? null : Number(i.unit_price),
    }));
    if (items.length === 0) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const body = { ...form, items, job_id: form.job_id === "none" ? null : form.job_id };
      if (editing) await updateStockOrder(editing.id, body);
      else await createStockOrder(body);
      toast.success(editing ? "Stock order updated" : "Stock order draft created");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const startWhatsApp = async (o) => {
    try {
      const r = await whatsappStockOrder(o.id);
      setWaOpened(false);
      setWa({ order: o, ...r });
    } catch (e) { toast.error(apiError(e)); }
  };
  const openWhatsAppWindow = () => { if (wa?.wa_url) { window.open(wa.wa_url, "_blank", "noopener"); setWaOpened(true); } };
  const confirmMarkSent = async () => {
    if (!wa) return;
    setMarking(true);
    try { await markStockOrderSent(wa.order.id); toast.success("Order marked as sent via WhatsApp"); setWa(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setMarking(false); }
  };

  const selectedSupplier = suppliers.find((s) => s.id === form.supplier_id);

  if (!orders) return <Loading />;

  return (
    <div data-testid="stock-orders">
      <div className="flex justify-between items-center mb-4 gap-3">
        <p className="text-sm text-slate-500 hidden sm:block">Create supplier material orders and send them to your supplier's salesperson on WhatsApp. A PDF is kept for your records.</p>
        <Button onClick={openNew} className={`text-white ${accentClass}`} data-testid="new-stock-order-btn"><Plus className="w-4 h-4 mr-2" /> New Stock Order</Button>
      </div>

      {orders.length === 0 ? (
        <Card className="p-10 text-center text-slate-400" data-testid="stock-empty">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          No stock orders yet. Create your first order to request materials from a supplier.
        </Card>
      ) : (
        <div className="space-y-3" data-testid="stock-order-list">
          {orders.map((o) => (
            <Card key={o.id} className="p-4" data-testid={`stock-order-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-[#0B132B]">{o.number}</span>
                    <StatusBadge status={o.status} />
                    {!o.order_ref && o.status === "draft" && <span className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Order Ref needed</span>}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">{supplierLabel(o)}</div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    Ref: {o.order_ref || "—"}{o.job_number ? ` · Job ${o.job_number}` : ""} · {o.items?.length || 0} item{(o.items?.length || 0) === 1 ? "" : "s"} · by {o.created_by_name || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  <a href={stockOrderPdfUrl(o.id)} target="_blank" rel="noreferrer">
                    <Button variant="outline" size="sm" data-testid={`stock-pdf-${o.id}`}><FileDown className="w-4 h-4" /></Button>
                  </a>
                  {o.status === "draft" && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => openEdit(o)} data-testid={`stock-edit-${o.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" className="bg-[#25D366] hover:bg-[#1da851] text-white" onClick={() => startWhatsApp(o)} data-testid={`stock-whatsapp-${o.id}`}><MessageCircle className="w-4 h-4 mr-1" /> Send via WhatsApp</Button>
                    </>
                  )}
                  {manage && o.status !== "draft" && o.status !== "cancelled" && (
                    <Button variant="outline" size="sm" onClick={() => setStatusFor(o)} data-testid={`stock-track-${o.id}`}><Truck className="w-4 h-4 mr-1" /> Update</Button>
                  )}
                  {(o.status === "draft" || o.status === "cancelled") && (
                    <ConfirmDelete onConfirm={async () => { await deleteStockOrder(o.id); load(); toast.success("Deleted"); }} title="Delete stock order?" trigger={<Button variant="ghost" size="sm" className="text-rose-600" data-testid={`stock-delete-${o.id}`}><Trash2 className="w-4 h-4" /></Button>} />
                  )}
                </div>
              </div>
              {o.sent_at && (
                <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-400 flex items-center gap-1">
                  <PackageCheck className="w-3.5 h-3.5" /> Sent via WhatsApp{o.whatsapp_to ? ` to +${o.whatsapp_to}` : ""} · by {o.sent_by || "—"}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="stock-order-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.number}` : "New Stock Order"}</DialogTitle>
            <DialogDescription>Select a supplier, add an Order Reference and the materials you need.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Supplier *</Label>
                <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger data-testid="stock-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.branch_name ? ` — ${s.branch_name}` : (s.suburb ? ` — ${s.suburb}` : "")}</SelectItem>)}</SelectContent>
                </Select>
                {suppliers.length === 0 && <p className="text-xs text-amber-600 mt-1">No suppliers yet — add one in the Suppliers tab first.</p>}
                {selectedSupplier && !selectedSupplier.whatsapp_e164 && <p className="text-xs text-amber-600 mt-1">This supplier has no WhatsApp number — add the salesperson's number in Suppliers before sending.</p>}
                {selectedSupplier?.whatsapp_e164 && <p className="text-xs text-slate-400 mt-1">WhatsApp: +{selectedSupplier.whatsapp_e164}{selectedSupplier.salesperson_name ? ` (${selectedSupplier.salesperson_name})` : ""}</p>}
              </div>
              <div>
                <Label>Order Reference *</Label>
                <Input value={form.order_ref} onChange={(e) => setForm({ ...form, order_ref: e.target.value })} placeholder="Your PO / job ref" data-testid="stock-order-ref" />
              </div>
            </div>

            {!fixedJobId && (
              <div>
                <Label>Link to job (optional)</Label>
                <Select value={form.job_id} onValueChange={(v) => setForm({ ...form, job_id: v })}>
                  <SelectTrigger data-testid="stock-job-select"><SelectValue placeholder="No job" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No job</SelectItem>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.number} — {j.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Items *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="stock-add-item"><Plus className="w-3.5 h-3.5 mr-1" /> Add item</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`stock-item-row-${idx}`}>
                    <Input className="col-span-12 sm:col-span-4" value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Item description" data-testid={`stock-item-desc-${idx}`} />
                    <Input className="col-span-3 sm:col-span-2" value={it.code} onChange={(e) => setItem(idx, "code", e.target.value)} placeholder="Code" />
                    <Input className="col-span-3 sm:col-span-2" type="number" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} placeholder="Qty" data-testid={`stock-item-qty-${idx}`} />
                    <Input className="col-span-2 sm:col-span-1" value={it.unit} onChange={(e) => setItem(idx, "unit", e.target.value)} placeholder="Unit" />
                    <Input className="col-span-3 sm:col-span-2" type="number" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} placeholder="Price" data-testid={`stock-item-price-${idx}`} />
                    <Button type="button" variant="ghost" size="sm" className="col-span-1 text-rose-600" onClick={() => removeItem(idx)} disabled={form.items.length === 1}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">Price is optional — include it where you know the supplier's rate.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Fulfilment</Label>
                <Select value={form.delivery_method} onValueChange={(v) => setForm({ ...form, delivery_method: v })}>
                  <SelectTrigger data-testid="stock-delivery-method"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="collect">Collect from supplier</SelectItem><SelectItem value="deliver">Deliver to site</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Site reference</Label><Input value={form.site_reference} onChange={(e) => setForm({ ...form, site_reference: e.target.value })} placeholder="Site / job name" /></div>
            </div>
            {form.delivery_method === "deliver" && (
              <div><Label>Delivery address</Label><Input value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} placeholder="Site delivery address" data-testid="stock-delivery-address" /></div>
            )}
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any special instructions" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className={`text-white ${accentClass}`} data-testid="save-stock-order-btn">{saving ? "Saving…" : editing ? "Save changes" : "Create draft"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp send flow */}
      <Dialog open={!!wa} onOpenChange={(v) => !v && setWa(null)}>
        <DialogContent className="max-w-lg" data-testid="stock-whatsapp-dialog">
          <DialogHeader>
            <DialogTitle>Send {wa?.order?.number} via WhatsApp</DialogTitle>
            <DialogDescription>Review the order message, open WhatsApp to the salesperson (+{wa?.phone}), then send it there. Opening WhatsApp does not mark this order as sent.</DialogDescription>
          </DialogHeader>
          <pre className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap max-h-56 overflow-y-auto" data-testid="whatsapp-message-preview">{wa?.message}</pre>
          <div className="flex flex-col gap-2">
            <Button onClick={openWhatsAppWindow} className="bg-[#25D366] hover:bg-[#1da851] text-white w-full" data-testid="open-whatsapp-btn"><ExternalLink className="w-4 h-4 mr-1.5" /> Open WhatsApp</Button>
            {waOpened && <p className="text-xs text-slate-500 text-center">Review and press send inside WhatsApp. Once done, confirm below.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWa(null)}>Close</Button>
            <Button onClick={confirmMarkSent} disabled={marking || !waOpened} className={`text-white ${accentClass}`} data-testid="mark-sent-btn"><PackageCheck className="w-4 h-4 mr-1" /> {marking ? "Saving…" : "I've sent it — mark submitted"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery status update (manage) */}
      {manage && <StatusDialog order={statusFor} onClose={() => setStatusFor(null)} onSaved={load} />}
    </div>
  );
}

function StatusDialog({ order, onClose, onSaved }) {
  const [status, setStatus] = useState("confirmed");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (order) { setStatus("confirmed"); setNote(""); } }, [order]);
  if (!order) return null;
  const save = async () => {
    setSaving(true);
    try { await updateStockOrderStatus(order.id, { status, note }); toast.success("Delivery status updated"); onClose(); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="stock-status-dialog">
        <DialogHeader><DialogTitle>Update delivery status — {order.number}</DialogTitle><DialogDescription>Track the fulfilment of this stock order.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="stock-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["confirmed", "dispatched", "delivered", "cancelled"].map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Collected by John" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1E7A6B] hover:bg-[#186052] text-white" data-testid="save-stock-status-btn">{saving ? "Saving…" : "Update"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
